import { isAuthContractVariable } from "./authContractFields";
import { isComputedField } from "@/types/Field";

/** Structural shape so every producer of guest fields can use this — the
 *  React-free node factory, the AI tools and the panels all differ in their
 *  Field type but agree on these two keys. */
type PublicFieldLike = {
  name: string;
  additionalField?: boolean;
  /** Present ⇒ the value is computed; see {@link isPublicFormField}. */
  formula?: unknown;
};

/**
 * Which guest variables a PUBLIC website form may actually write.
 *
 * The builder's field pickers are fed by `getFields("guest", eventId)`, which
 * merges the event's custom additional fields with `FIELDS_GUEST` — the
 * GUEST-LIST COLUMN CATALOGUE (55 entries: `createdAt`, `guestId`, `origin`,
 * `attachments`, `apptCount`, …). Almost none of that is writeable from a
 * public submit, and until this allowlist existed every picker offered all of
 * it:
 *
 *  - keys that are neither a writeable core column nor a defined custom field
 *    reach the additional-field validator as unknown keys and **400 the whole
 *    submission** (website-builder-backend `lib/additional-field-validator.ts`
 *    → `routes/runtime.ts` register/data-input handlers). The visitor doesn't
 *    even have to touch the input: an untouched text box submits `""`, which
 *    is folded like any other value — so ONE such field kills every
 *    registration on the site.
 *  - keys the server recognises but refuses to mutate on a public path
 *    (`username`, `code`, `checkedIn(At)`, `archived`, `imageUrl`, `role`,
 *    `questions`, `sessions`, `acceptanceState`) are **silently discarded**
 *    with a 200 — the visitor's input vanishes.
 *
 * This set is the editor-side TWIN of `CORE_GUEST_COLUMNS` in
 * website-builder-backend `src/lib/data-input-split.ts`. Keep them in sync:
 * anything added there must be added here, or the builder will hide a field
 * the backend would happily accept.
 *
 * NOT derived from the catalogue's own `editable: false` markers: those track
 * guest-list inline editing (an admin may edit `archived`/`username` there),
 * which is a different question from "may an anonymous visitor write this".
 *
 * ⚠ FOURTH COPY of the same list: website-builder-backend
 * `lib/data-input-split.ts`, website-builder-frontend `lib/dataInputSplit.ts`,
 * and this one. A column added on the server without adding it here is hidden
 * from every builder picker.
 */
export const PUBLIC_WRITEABLE_CORE_VARIABLES: ReadonlySet<string> = new Set([
  "firstName",
  "lastName",
  "gender",
  "title",
  "jobTitle",
  "company",
  "email",
  "phone",
  "mobile",
  "address",
  "address2",
  "zipCode",
  "city",
  "country",
  "language",
  "emailOptIn",
  // The participation answer. Writeable — and the ADD pickers additionally
  // filter it out via `isAcceptanceFieldName` because it carries DataInput's
  // flow semantics and is placed by the section, not picked by hand. (The
  // per-field variable picker and the wizard do still offer it; that predates
  // this allowlist and is out of its scope.)
  "state",
]);

/**
 * True when a catalogue entry may be offered as a public form field: every
 * custom additional field of the event (those ARE the extras the validator
 * knows), plus the writeable core columns.
 */
export const isPublicFormField = (field: PublicFieldLike): boolean =>
  // A COMPUTED field is the one custom field a visitor may not fill in: the
  // backend refuses the write and re-derives the value anyway, so offering it
  // would put a box on the public form whose answer is discarded on submit.
  !isComputedField(field) &&
  (field.additionalField === true ||
    PUBLIC_WRITEABLE_CORE_VARIABLES.has(field.name));

/**
 * Picker list for a form field: the writeable catalogue, plus whatever
 * variable the field ALREADY points at — as long as that variable is IN the
 * catalogue. A document may carry a non-writeable variable from before this
 * guard (or from a template/AI import); dropping it from its own picker would
 * render an empty row and make the misconfiguration unfixable. (A variable
 * that isn't a catalogue entry at all — some templates seed such names — was
 * already unrepresentable in the picker before this guard and still is.)
 */
export const publicFormFieldsFor = <T extends PublicFieldLike>(
  fields: T[],
  currentVariable?: string,
  opts?: { insideLoginCard?: boolean },
): T[] =>
  fields.filter(
    (f) =>
      isPublicFormField(f) ||
      (!!currentVariable && f.name === currentVariable) ||
      // Inside the LOGIN card the two auth-contract variables are pickable
      // again, even though they are (correctly) absent from the writeable
      // allowlist above — `/auth/login` reads them but the public data-input
      // path refuses to WRITE them, which is why they are excluded in general.
      //
      // Without this, a login field that had already been re-pointed at some
      // other variable (`company`, say) was UNREPAIRABLE: it is not an
      // auth-contract field any more, so the panel renders the picker rather
      // than the locked row — but the picker no longer lists `username`, the
      // "+ Add field" affordance was removed from the login panel, and
      // `canMoveIn` refuses drops. `/auth/login` then 400s for every visitor
      // with no way back short of deleting and rebuilding the whole section.
      (opts?.insideLoginCard === true && isAuthContractVariable(f.name)),
  );
